/**
 * Analytics dashboard for a project (N4+N5).
 *
 * Fetches all 6 analytics endpoints concurrently, then renders:
 *  - ThroughputChart (tasks completed per week)
 *  - StatusDistributionChart (current task distribution per status)
 *  - CycleTimeChart (distribution + avg/median)
 *  - WorkloadTable (per-assignee open tasks, hours, overdue)
 *  - WorkloadHeatmap (#64 — same data broken down as a User×Week grid)
 *
 * Print/PDF: a "Drucken / PDF" button calls window.print(). The
 * .juuplan-analytics-print-hide class suppresses nav/sidebar/buttons
 * in the @media print rules in index.css.
 */

import { useState, useEffect } from "preact/hooks";
import { Printer } from "lucide-preact";
import {
  getAnalyticsThroughput,
  getAnalyticsCycleTime,
  getAnalyticsStatusDistribution,
  getWorkload,
  getWorkloadWeekly,
  type ThroughputResponse,
  type CycleTimeResponse,
  type StatusDistributionItem,
  type WorkloadItem,
  type WorkloadHeatmapResponse,
} from "../lib/api";
import { ThroughputChart } from "./analytics/ThroughputChart";
import { StatusDistributionChart } from "./analytics/StatusDistributionChart";
import { CycleTimeChart } from "./analytics/CycleTimeChart";
import { WorkloadTable } from "./analytics/WorkloadTable";
import { WorkloadHeatmap } from "./analytics/WorkloadHeatmap";

interface Props {
  projectId: number;
}

interface AnalyticsState {
  throughput: ThroughputResponse | null;
  cycleTime: CycleTimeResponse | null;
  statusDist: StatusDistributionItem[];
  workload: WorkloadItem[];
  workloadWeekly: WorkloadHeatmapResponse | null;
  loading: boolean;
  error: string | null;
}

function ChartCard({
  title,
  children,
  class: className = "",
}: {
  title: string;
  children: preact.ComponentChildren;
  class?: string;
}): preact.JSX.Element {
  return (
    <div
      class={`rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-4 flex flex-col gap-3 min-w-0 ${className}`}
    >
      <h3 class="text-xs font-semibold uppercase tracking-wider text-[color:var(--color-on-surface-variant)]">
        {title}
      </h3>
      {children}
    </div>
  );
}

export function ProjectAnalytics({ projectId }: Props): preact.JSX.Element {
  const [state, setState] = useState<AnalyticsState>({
    throughput: null,
    cycleTime: null,
    statusDist: [],
    workload: [],
    workloadWeekly: null,
    loading: true,
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    setState((prev) => ({ ...prev, loading: true, error: null }));

    Promise.all([
      getAnalyticsThroughput(projectId, { interval: "week" }),
      getAnalyticsCycleTime(projectId),
      getAnalyticsStatusDistribution(projectId),
      getWorkload(projectId),
      getWorkloadWeekly(projectId),
    ])
      .then(([throughput, cycleTime, statusDist, workload, workloadWeekly]) => {
        if (!cancelled) {
          setState({
            throughput,
            cycleTime,
            statusDist,
            workload,
            workloadWeekly,
            loading: false,
            error: null,
          });
        }
      })
      .catch((err: unknown) => {
        if (!cancelled) {
          setState((prev) => ({
            ...prev,
            loading: false,
            error: err instanceof Error ? err.message : "Fehler beim Laden der Auswertung",
          }));
        }
      });

    return () => {
      cancelled = true;
    };
  }, [projectId]);

  if (state.loading) {
    return (
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4">
        {[...Array(4)].map((_, i) => (
          <div
            key={i}
            class="rounded-lg border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] p-4 h-40 juuplan-skeleton"
          />
        ))}
      </div>
    );
  }

  if (state.error) {
    return (
      <p class="text-sm text-[color:var(--color-error)] p-4">
        Fehler: {state.error}
      </p>
    );
  }

  const handlePrint = (): void => {
    window.print();
  };

  return (
    <div class="flex flex-col gap-4">
      {/* Print button — hidden in print media via CSS */}
      <div class="flex justify-end juuplan-analytics-no-print">
        <button
          type="button"
          onClick={handlePrint}
          class="inline-flex items-center gap-1.5 rounded border border-[color:var(--color-outline-variant)] bg-[color:var(--color-surface-container-lowest)] px-3 py-1.5 text-xs font-medium text-[color:var(--color-on-surface)] hover:bg-[color:var(--color-surface-container-low)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--color-primary)] focus-visible:ring-offset-2 transition-colors min-h-[32px]"
          title="Auswertung als PDF drucken"
          aria-label="Auswertung drucken / als PDF speichern"
        >
          <Printer size={14} aria-hidden="true" />
          Drucken / PDF
        </button>
      </div>

      {/* Chart grid */}
      <div class="grid grid-cols-1 sm:grid-cols-2 gap-4 juuplan-analytics-grid">
        {/* Throughput */}
        <ChartCard title="Durchsatz (erledigte Aufgaben / Woche)">
          {state.throughput ? (
            <ThroughputChart
              buckets={state.throughput.buckets}
              total={state.throughput.total}
            />
          ) : (
            <p class="text-label italic text-[color:var(--color-on-surface-variant)]">
              Keine Daten
            </p>
          )}
        </ChartCard>

        {/* Status distribution */}
        <ChartCard title="Aufgaben je Status">
          <StatusDistributionChart items={state.statusDist} />
        </ChartCard>

        {/* Cycle time */}
        <ChartCard title="Durchlaufzeit">
          {state.cycleTime ? (
            <CycleTimeChart
              distribution={state.cycleTime.distribution}
              summary={state.cycleTime.summary}
            />
          ) : (
            <p class="text-label italic text-[color:var(--color-on-surface-variant)]">
              Keine Daten
            </p>
          )}
        </ChartCard>

        {/* Workload */}
        <ChartCard title="Auslastung je Nutzer">
          <WorkloadTable items={state.workload} />
        </ChartCard>

        {/* Workload heatmap (#64) — full width, its own week axis can get wide */}
        <ChartCard title="Auslastung je Woche" class="sm:col-span-2">
          {state.workloadWeekly ? (
            <WorkloadHeatmap data={state.workloadWeekly} />
          ) : (
            <p class="text-label italic text-[color:var(--color-on-surface-variant)]">
              Keine Daten
            </p>
          )}
        </ChartCard>
      </div>
    </div>
  );
}
